// src/lib/motion/constants.ts — Framer Motion animation variants

// Impact frame sequence — used by triggerImpactFrame()
export const IMPACT_FRAME = {
  scale: [1, 1.05, 1],
  boxShadow: [
    '0 0 0px rgba(232, 160, 32, 0)',
    '0 0 12px rgba(232, 160, 32, 0.8)',
    '0 0 0px rgba(232, 160, 32, 0)',
  ],
  transition: {
    duration: 0.35,
    times: [0, 0.15, 1],
    ease: 'easeOut',
  },
} as const;

// Framer Motion container variants for list/grid staggers
export const listContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.08,
    },
  },
};

export const listItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: 'easeOut' },
  },
};

export const gridItemVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: (i: number) => {
    const row = Math.floor(i / 4);
    return {
      opacity: 1,
      scale: 1,
      transition: { delay: row * 0.08, duration: 0.3 },
    };
  },
};

// Anticipation pattern (Disney 12 principles applied to UI). On press,
// the element shrinks 2% for 60ms before the action's primary
// transition. Adds physicality — the user feels the press register
// before the result. Use via `whileTap={ANTICIPATION_PRESS}` on primary
// CTAs. Research 2026-05-01.
export const ANTICIPATION_PRESS = {
  scale: 0.98,
  transition: { duration: 0.06, ease: [0, 0, 0.2, 1] as const },
} as const;

// Follow-through overshoot (≤8% past target then settle). Apply to
// celebrate-class transitions like badge unlocks. Pairs with
// SpringConfigs.celebrate. Research 2026-05-01.
export const FOLLOW_THROUGH_OVERSHOOT = {
  scale: [0.95, 1.06, 1],
  transition: {
    times: [0, 0.55, 1],
    duration: 0.6,
    ease: 'easeOut',
  },
} as const;

// Modal animation variants
export const modalBackdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

export const modalContentVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.2 },
  },
};

// Tab transition variants
export const tabSlideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0,
  }),
};

// Sidebar variants
export const sidebarVariants = {
  hidden: { x: '-100%', opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 200,
      damping: 15,
      staggerChildren: 0.04,
    },
  },
  exit: { x: '-100%', opacity: 0, transition: { duration: 0.25 } },
};

// Highlight pulse for AppTour
export const highlightPulseVariants = {
  pulse: {
    boxShadow: [
      '0 0 0px rgba(232, 160, 32, 0)',
      '0 0 20px rgba(232, 160, 32, 0.8)',
      '0 0 0px rgba(232, 160, 32, 0)',
    ],
    transition: { duration: 1.5, repeat: Infinity, repeatDelay: 0.3 },
  },
};

// Skeleton shimmer keyframes (CSS-in-JS fallback)
export const SKELETON_GRADIENT = {
  background: 'linear-gradient(90deg, var(--color-surface-ephemeral) 25%, var(--color-surface-ephemeral) 50%, var(--color-surface-ephemeral) 75%)',
  backgroundSize: '1000px 100%',
};
